import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef, useCallback } from 'react';
import { ChevronRight, ChevronLeft, Beaker, X, GitBranch, History, GripVertical } from 'lucide-react';
import { ChemPluginPanelProps, ChemExperimentState } from './types';
import { ReactionSetupTab } from './ReactionSetupTab';
import { BomTab } from './BomTab';
import { CalculationsTab } from './CalculationsTab';
import { ReactionPathView } from './ReactionPathView';
import { ReactionStepEditor } from './ReactionStepEditor';
import { saveCurrentExperiment, loadCurrentExperiment, saveTransaction } from './experimentStorage';
import { ExperimentHistoryModal } from './ExperimentHistoryModal';
import { UtilitiesTab } from './UtilitiesTab';
import { ChemPluginHandle } from './ChemPluginHandle';
import {
  SetupReactionPayload,
  AddMaterialPayload,
  RemoveMaterialPayload,
  RunCalculationPayload,
} from './chemActions';
import { generateStepId } from './chemExperimentHelpers';
import { getCalculation } from './chemConfig';
import { calculationEngine } from './calculationEngine';

type TabType = 'setup' | 'bom' | 'calculations' | 'steps' | 'path' | 'utilities';

/**
 * ChemPluginPanel Component
 * 
 * Main chemistry plugin panel with collapsible/expandable states.
 * Contains three tabs: Reaction Setup, BOM & Materials, Calculations.
 * 
 * Features:
 * - Collapsed state: Icon button only
 * - Expanded state: Full panel with tabs
 * - Resizable width
 * - State persistence via callback
 * - Imperative handle for command-based control
 */
export const ChemPluginPanel = forwardRef<ChemPluginHandle, ChemPluginPanelProps>(({
  experimentId,
  isOpen = false,
  onStateChange,
  onToggle
}, ref) => {
  const [activeTab, setActiveTab] = useState<TabType>('setup');
  const [state, setState] = useState<ChemExperimentState>(() => {
    // Try to load from localStorage first
    const saved = loadCurrentExperiment();
    if (saved && saved.experimentId === experimentId) {
      return saved;
    }
    // Otherwise create new state
    return {
      experimentId,
      experimentType: 'API',
      reactionType: 'Peptide',
      batchId: '',
      reactionName: '',
      materials: [],
    };
  });
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // Resizable width state
  const MIN_WIDTH = 450; // Current fixed width as minimum
  const MAX_WIDTH = 1200; // Maximum width
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const saved = localStorage.getItem('chemPluginPanelWidth');
    return saved ? Math.max(MIN_WIDTH, parseInt(saved, 10)) : MIN_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  // Update experimentId when prop changes
  useEffect(() => {
    setState((prev) => ({ ...prev, experimentId }));
  }, [experimentId]);

  // Save to localStorage whenever state changes
  useEffect(() => {
    saveCurrentExperiment(state);
  }, [state]);

  // Save panel width to localStorage
  useEffect(() => {
    localStorage.setItem('chemPluginPanelWidth', panelWidth.toString());
  }, [panelWidth]);

  // Handle resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      const constrainedWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));
      setPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Notify parent of state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(state);
    }
  }, [state, onStateChange]);

  const updateState = (updates: Partial<ChemExperimentState>, transactionType?: 'reaction_setup' | 'material_added' | 'material_removed' | 'step_added' | 'step_removed', description?: string, details?: Record<string, any>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };

      // Auto-save transaction for significant changes
      if (transactionType && description) {
        saveTransaction(newState, transactionType, description, details);
      }

      return newState;
    });
  };

  // Expose imperative handle for command-based control
  useImperativeHandle(ref, () => ({
    setupReaction(payload: SetupReactionPayload) {
      const description = `Reaction setup: ${payload.experimentType} / ${payload.reactionType}${payload.batchId ? ' (Batch ' + payload.batchId + ')' : ''}${payload.reactionName ? ' – ' + payload.reactionName : ''}`;
      updateState({
        experimentType: payload.experimentType,
        reactionType: payload.reactionType,
        batchId: payload.batchId || '',
        reactionName: payload.reactionName,
      }, 'reaction_setup', description, {
        experimentType: payload.experimentType,
        reactionType: payload.reactionType,
        batchId: payload.batchId,
        reactionName: payload.reactionName,
      });
    },

    addMaterial(payload: AddMaterialPayload) {
      const newMaterial = {
        id: `mat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: payload.name,
        role: payload.role,
        quantity: payload.quantity,
        unit: payload.unit,
        batchNo: payload.batchNo,
        metadata: {},
      };
      const description = `Added material: ${payload.name} (${payload.quantity} ${payload.unit}) as ${payload.role}`;
      updateState({ materials: [...state.materials, newMaterial] }, 'material_added', description, {
        materialName: payload.name,
        role: payload.role,
        quantity: payload.quantity,
        unit: payload.unit,
      });
    },

    removeMaterial(payload: RemoveMaterialPayload) {
      const description = `Removed material: ${payload.name}`;
      updateState({
        materials: state.materials.filter(
          (m) => !m.name.toLowerCase().includes(payload.name.toLowerCase())
        ),
      }, 'material_removed', description, {
        materialName: payload.name,
      });
    },

    listBom() {
      return state.materials;
    },

    async runCalculation(payload: RunCalculationPayload) {
      const calcDef = getCalculation(payload.calcId);
      if (!calcDef) {
        throw new Error(`Calculation "${payload.calcId}" not found`);
      }

      // Save transaction for calculation run
      const description = `Ran calculation: ${calcDef.name} (${payload.calcId})`;
      saveTransaction(state, 'calculation_run', description, {
        calculationId: payload.calcId,
        calculationName: calcDef.name,
      });

      // For now, just validate that the calculation can run
      // In a full implementation, this would prompt for inputs and store results
      // This is a placeholder - actual execution would require user input
      console.log(`Running calculation: ${calcDef.name} (${payload.calcId})`);

      // You could trigger the calculation tab to open and show the form
      // For now, we'll just log it
      return Promise.resolve();
    },

    getExperimentState() {
      return state;
    },
  }), [state]);

  const tabs = [
    { id: 'setup' as TabType, label: 'Reaction Setup', icon: Beaker },
    { id: 'bom' as TabType, label: 'BOM & Materials', icon: Beaker },
    { id: 'calculations' as TabType, label: 'Calculations', icon: Beaker },
    { id: 'steps' as TabType, label: 'Reaction Steps', icon: Beaker },
    { id: 'path' as TabType, label: 'Reaction Path', icon: GitBranch },
    { id: 'utilities' as TabType, label: 'Utilities', icon: Beaker },
  ];

  // if (!isOpen) {
  //   // Collapsed state - just a button
  //   return (
  //     // <div style={styles.collapsed}>
  //     //   <button
  //     //     style={styles.collapsedButton}
  //     //     onClick={onToggle}
  //     //     title="Open Chemistry Plugin"
  //     //   >
  //     //     <Beaker size={24} />
  //     //     <span style={styles.collapsedLabel}>Chem Plugin</span>
  //     //   </button>
  //     // </div>
  //     // <div className="chat-popup">

  //     <button className="ChemicalIcon border-0 ">
  //       <Beaker size={24} />
  //     </button>
  //     // </div>
  //   );
  // }

  // Expanded state - full panel
  return (
    <div style={{ ...styles.panel, width: `${panelWidth}px` }}>
      {/* Resize Handle */}
      <div
        ref={resizeRef}
        style={{
          ...styles.resizeHandle,
          ...(isResizing ? styles.resizeHandleHover : {}),
        }}
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      >
        <GripVertical size={16} color={isResizing ? '#fff' : '#64748b'} />
      </div>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <Beaker size={20} color="#2563eb" />
          <h3 style={styles.title}>Chemistry Plugin</h3>
        </div>
        <div style={styles.headerRight}>
          <button
            style={styles.iconButton}
            onClick={() => setIsHistoryOpen(true)}
            title="View history"
          >
            <History size={18} />
          </button>
          <button
            style={styles.iconButton}
            onClick={onToggle}
            title="Close panel"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Experiment Info Bar */}
      <div style={styles.infoBar}>
        <span style={styles.infoLabel}>Experiment:</span>
        <span style={styles.infoValue}>{experimentId}</span>
        <span style={styles.divider}>|</span>
        <span style={styles.infoLabel}>Type:</span>
        <span style={styles.infoValue}>{state.experimentType}</span>
        {state.batchId && (
          <>
            <span style={styles.divider}>|</span>
            <span style={styles.infoLabel}>Batch:</span>
            <span style={styles.infoValue}>{state.batchId}</span>
          </>
        )}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.content}>
        {activeTab === 'setup' && (
          <ReactionSetupTab state={state} onUpdate={updateState} />
        )}
        {activeTab === 'bom' && (
          <BomTab
            materials={state.materials}
            reactionType={state.reactionType}
            onUpdate={(materials) => updateState({ materials })}
          />
        )}
        {activeTab === 'calculations' && (
          <CalculationsTab
            experiment={state}
            experimentType={state.experimentType}
            reactionType={state.reactionType}
            onResult={(result) => {
              console.log('Calculation result:', result);
              // Save transaction for calculation run from UI
              const calcDef = getCalculation(result.calcId);
              if (calcDef) {
                const description = `Ran calculation: ${calcDef.name} (${result.calcId}) - Result: ${result.value} ${result.unit || ''}`;
                saveTransaction(state, 'calculation_run', description, {
                  calculationId: result.calcId,
                  calculationName: calcDef.name,
                  result: result.value,
                  unit: result.unit,
                });
              }
            }}
          />
        )}
        {activeTab === 'steps' && (
          <ReactionStepEditor
            experiment={state}
            onChange={(updated) => {
              setState(updated);
              onStateChange?.(updated);
            }}
          />
        )}
        {activeTab === 'path' && (
          <ReactionPathView experiment={state} />
        )}
        {activeTab === 'utilities' && (
          <UtilitiesTab />
        )}
      </div>

      {/* History Modal */}
      {isHistoryOpen && (
        <ExperimentHistoryModal
          currentExperiment={state}
          onClose={() => setIsHistoryOpen(false)}
          onLoad={(experiment) => {
            setState(experiment);
            setIsHistoryOpen(false);
          }}
        />
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <span style={styles.footerText}>
          Phase 5 • Reaction Step Editor + Path Visualization
        </span>
      </div>
    </div>
  );
});

ChemPluginPanel.displayName = 'ChemPluginPanel';

const styles: { [key: string]: React.CSSProperties } = {
  // Collapsed state styles
  collapsed: {
    position: 'fixed',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 40,
  },
  collapsedButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px 12px',
    backgroundColor: '#2563eb',
    color: '#fff',
    border: 'none',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
    cursor: 'pointer',
    boxShadow: '-2px 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s',
  },
  collapsedLabel: {
    fontSize: '11px',
    fontWeight: 600,
    writingMode: 'vertical-rl',
    textOrientation: 'mixed',
  },

  // Expanded panel styles
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    width: '450px', // Default width, will be overridden by inline style
    minWidth: '450px', // Minimum width to prevent collapse
    maxWidth: '1200px', // Maximum width
    height: '100%',
    backgroundColor: '#fff',
    borderLeft: '1px solid #e2e8f0',
    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.05)',
    overflow: 'hidden',
    zIndex: 1000,
  },
  resizeHandle: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '8px',
    backgroundColor: '#e2e8f0',
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1001,
    transition: 'background-color 0.2s',
  },
  resizeHandleHover: {
    backgroundColor: '#2563eb',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    paddingLeft: '28px', // Add extra padding to account for resize handle (8px handle + 20px padding)
    backgroundColor: '#eff6ff',
    borderBottom: '1px solid #bfdbfe',
    position: 'relative',
    zIndex: 1002, // Ensure header is above resize handle and panel background
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
  },
  headerRight: {
    display: 'flex',
    gap: '8px',
  },
  iconButton: {
    padding: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    fontSize: '12px',
  },
  infoLabel: {
    color: '#64748b',
    fontWeight: 500,
  },
  infoValue: {
    color: '#1e293b',
    fontWeight: 600,
  },
  divider: {
    color: '#cbd5e1',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#fff',
  },
  tab: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#2563eb',
    borderBottomColor: '#2563eb',
    backgroundColor: '#eff6ff',
  },
  tabIcon: {
    fontSize: '16px',
  },
  tabLabel: {
    fontSize: '13px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden', // Prevent horizontal scroll
    backgroundColor: '#fff',
    minWidth: 0, // Allow flexbox to properly constrain width
  },
  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    textAlign: 'center',
  },
  footerText: {
    fontSize: '11px',
    color: '#94a3b8',
  },
};

